import { useEffect, useRef, useState } from "react";
import classNames from "classnames";
import { toast } from "react-toastify";
import { getPlansAction } from "../../../Actions/getPlanAction";

// ── helpers ──────────────────────────────────────────────────────────

const isNeverOwned = (desc = "") =>
  desc.toLowerCase().includes("never owned") ||
  desc.toLowerCase().includes("never-owned");

const isExtendedWarranty = (desc = "") =>
  desc.toLowerCase().includes("extended");

const resolvePlanIcon = (description = "") => {
  const desc = description.toLowerCase();
  if (desc.includes("extended")) return "/ew.png";
  if (desc.includes("annual") || desc.includes("maintenance"))
    return "/amp.png";
  return "/bw.png";
};

const resolvePlanDesc = (description = "") => {
  const desc = description.toLowerCase();
  if (desc.includes("extended"))
    return "Enhanced peace of mind with additional years of protection.";
  if (desc.includes("annual") || desc.includes("maintenance"))
    return "Now available for air conditioners.";
  return "Standard   coverage included with your purchase.";
};

const groupPlans = (plans) => {
  const descCategories = new Map();
  plans.forEach((p) => {
    const cat = p.category || "";
    if (!descCategories.has(p.plan_Description))
      descCategories.set(p.plan_Description, new Set());
    descCategories.get(p.plan_Description).add(cat);
  });

  const map = new Map();
  plans.forEach((p) => {
    const cat = p.category || "";
    const key = `${p.plan_Description}::${cat}`;
    const multiCat = descCategories.get(p.plan_Description).size > 1;
    const displayTitle = multiCat
      ? `${p.plan_Description} - ${cat}`
      : p.plan_Description;

    if (!map.has(key)) {
      map.set(key, {
        key,
        description: p.plan_Description,
        category: cat,
        displayTitle,
        icon: resolvePlanIcon(p.plan_Description),
        cardDesc: resolvePlanDesc(p.plan_Description),
        variants: [],
      });
    }
    map.get(key).variants.push({
      plan_VarriantsId: p.plan_VarriantsId,
      plan_Validity: p.plan_Validity,
      plan_Amount: p.plan_Amount ?? 0,
    });
  });

  map.forEach((g) =>
    g.variants.sort(
      (a, b) => Number(a.plan_Validity) - Number(b.plan_Validity),
    ),
  );

  return Array.from(map.values());
};

const chipLabel = (months) => {
  const m = Number(months);
  if (!m) return { top: months, bottom: "" };
  if (m % 12 === 0) {
    const y = m / 12;
    return { top: String(y), bottom: y === 1 ? "Year" : "Years" };
  }
  return { top: String(m), bottom: "Months" };
};

const badgeLabel = (months) => {
  const m = Number(months);
  if (!m) return "Coverage Included";
  if (m % 12 === 0) {
    const y = m / 12;
    return y === 1 ? "1 Year Coverage" : `${y} Years Coverage`;
  }
  return `${m} Months Coverage`;
};

const validityText = (months) => {
  const m = Number(months);
  if (!m) return months;
  if (m % 12 === 0) {
    const y = m / 12;
    return y === 1 ? "1 Year" : `${y} Years`;
  }
  return `${m} Months`;
};

// ── skeleton ─────────────────────────────────────────────────────────

const PlanSkeleton = () => (
  <div
    className="warranty-card"
    style={{ opacity: 0.45, pointerEvents: "none" }}
  >
    <div
      style={{
        width: 56,
        height: 56,
        borderRadius: "50%",
        background: "#E4E8F1",
        margin: "0 auto 12px",
      }}
    />
    <div
      style={{
        height: 14,
        background: "#E4E8F1",
        borderRadius: 6,
        marginBottom: 8,
      }}
    />
    <div
      style={{
        height: 10,
        background: "#E4E8F1",
        borderRadius: 6,
        width: "70%",
        margin: "0 auto 12px",
      }}
    />
    <div
      style={{
        height: 28,
        background: "#E4E8F1",
        borderRadius: 9999,
        width: "60%",
        margin: "0 auto",
      }}
    />
  </div>
);

// ── component ─────────────────────────────────────────────────────────

const ProtectionPlansSection = ({ selectedPlan, selectPlan }) => {
  const [groupedPlans, setGroupedPlans] = useState([]);
  const [loading, setLoading] = useState(true);
  const [fetchError, setFetchError] = useState(null);
  const [selectedVariants, setSelectedVariants] = useState({});
  const [allGroups, setAllGroups] = useState([]);

  // Never-Owned add-on: checkbox + radio year selection
  const [wantExtended, setWantExtended] = useState(false);
  const [selectedExtVariantId, setSelectedExtVariantId] = useState(null);

  // Extended-Warranty card: dropdown year selection
  const [selectedExtDropdownId, setSelectedExtDropdownId] = useState("");
  // Extended-Warranty card: existing brand warranty dropdown
  const [selectedBrandWarrantyYear, setSelectedBrandWarrantyYear] =
    useState("");

  const selectPlanRef = useRef(selectPlan);
  useEffect(() => {
    selectPlanRef.current = selectPlan;
  }, [selectPlan]);

  // ── fetch ────────────────────────────────────────────────────────────
  useEffect(() => {
    let cancelled = false;

    const fetchPlans = async () => {
      setLoading(true);
      setFetchError(null);

      const result = await getPlansAction();
      if (cancelled) return;

      if (result.success && result.data.length > 0) {
        const groups = groupPlans(result.data);
        setGroupedPlans(groups);
        setAllGroups(groups);

        const defaults = {};
        groups.forEach((g) => {
          if (g.variants.length > 0)
            defaults[g.key] = g.variants[0].plan_VarriantsId;
        });
        setSelectedVariants(defaults);

        if (groups.length > 0 && groups[0].variants.length > 0) {
          const g = groups[0];
          const v = g.variants[0];
          selectPlanRef.current({
            plan_Description: g.description,
            plan_VarriantsId: v.plan_VarriantsId,
            plan_Validity: v.plan_Validity,
            plan_Amount: v.plan_Amount,
            category: g.category,
            extendedVariantId: null,
          });
        }
      } else {
        const msg = result.error || "Failed to load protection plans.";
        setFetchError(msg);
        toast.error(msg);
      }

      setLoading(false);
    };

    fetchPlans();
    return () => {
      cancelled = true;
    };
  }, []);

  // ── derived values ───────────────────────────────────────────────────

  const currentlyNeverOwned = isNeverOwned(
    selectedPlan?.plan_Description ?? "",
  );
  const currentlyExtended = isExtendedWarranty(
    selectedPlan?.plan_Description ?? "",
  );

  // Extended variants matching the selected card's category (for both use-cases)
  const sameCategory = (g) =>
    !selectedPlan?.category ||
    g.category.toLowerCase() === selectedPlan.category.toLowerCase();

  // For Never-Owned add-on: extended groups of same category
  const extendedGroupsForAddon = currentlyNeverOwned
    ? allGroups.filter(
        (g) => isExtendedWarranty(g.description) && sameCategory(g),
      )
    : [];

  // For Extended-Warranty card dropdown: variants of the currently selected extended group
  const currentExtGroup = currentlyExtended
    ? groupedPlans.find((g) =>
        g.variants.some(
          (v) => v.plan_VarriantsId === selectedPlan?.plan_VarriantsId,
        ),
      )
    : null;

  // Reset add-on when switching away from Never-Owned
  useEffect(() => {
    if (!currentlyNeverOwned) {
      setWantExtended(false);
      setSelectedExtVariantId(null);
    }
  }, [currentlyNeverOwned]);

  // Sync dropdown when Extended Warranty card is selected
  useEffect(() => {
    if (currentlyExtended && selectedPlan?.plan_VarriantsId) {
      setSelectedExtDropdownId(String(selectedPlan.plan_VarriantsId));
    }
  }, [currentlyExtended, selectedPlan?.plan_VarriantsId]);

  // ── handlers ─────────────────────────────────────────────────────────

  const buildPlanObj = (group, variantId, extId = null) => {
    const variant =
      group.variants.find((v) => v.plan_VarriantsId === variantId) ||
      group.variants[0];
    return {
      plan_Description: group.description,
      plan_VarriantsId: variant.plan_VarriantsId,
      plan_Validity: variant.plan_Validity,
      plan_Amount: variant.plan_Amount,
      category: group.category,
      extendedVariantId: extId,
    };
  };

  const handleGroupClick = (group) => {
    const variantId = selectedVariants[group.key];
    selectPlan(buildPlanObj(group, variantId, null));
    setWantExtended(false);
    setSelectedExtVariantId(null);
    // Reset brand warranty year when switching plan card
    setSelectedBrandWarrantyYear("");
  };

  const handleVariantClick = (e, group, variant) => {
    e.stopPropagation();

    const isThisCardSelected = isGroupSelected(group);

    if (!isThisCardSelected) {
      // Card not selected — clicking its year chip just selects the card with default first variant
      handleGroupClick(group);
      return;
    }

    // Card is already selected — change the year
    setSelectedVariants((prev) => ({
      ...prev,
      [group.key]: variant.plan_VarriantsId,
    }));

    selectPlan({
      ...buildPlanObj(group, variant.plan_VarriantsId),
      extendedVariantId: wantExtended ? selectedExtVariantId : null,
    });
  };

  // Never-Owned: checkbox toggle
  const handleWantExtendedChange = (checked) => {
    setWantExtended(checked);
    if (!checked) {
      setSelectedExtVariantId(null);
      if (selectedPlan)
        selectPlan({ ...selectedPlan, extendedVariantId: null });
    }
  };

  // Never-Owned: radio year selection
  const handleExtRadioChange = (variantId) => {
    setSelectedExtVariantId(variantId);
    if (selectedPlan) {
      // Store the validity of the selected extended variant so warrantySummary can show the year
      const extVariant = extendedGroupsForAddon
        .flatMap((g) => g.variants)
        .find((v) => v.plan_VarriantsId === variantId);
      selectPlan({
        ...selectedPlan,
        extendedVariantId: variantId,
        extendedValidity: extVariant?.plan_Validity || null,
      });
    }
  };

  // Extended-Warranty card: dropdown year selection
  const handleExtDropdownChange = (variantId) => {
    setSelectedExtDropdownId(String(variantId));
    if (currentExtGroup) {
      const variant = currentExtGroup.variants.find(
        (v) => v.plan_VarriantsId === variantId,
      );
      if (variant) {
        selectPlan({
          ...buildPlanObj(currentExtGroup, variantId),
          extendedVariantId: null,
          brandWarrantyYear: selectedBrandWarrantyYear,
        });
        setSelectedVariants((prev) => ({
          ...prev,
          [currentExtGroup.key]: variantId,
        }));
      }
    }
  };

  const isGroupSelected = (group) =>
    group.variants.some(
      (v) => v.plan_VarriantsId === selectedPlan?.plan_VarriantsId,
    );

  // ── render ────────────────────────────────────────────────────────────

  return (
    <section className="card">
      <h2 className="card__title">
        <span className="section-icon">
          <svg
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              d="M12 3l7 3v6c0 4.5-3 7.5-7 9-4-1.5-7-4.5-7-9V6l7-3z"
            />
          </svg>
        </span>
        Protection Plans
      </h2>

      {loading && (
        <div className="grid-3">
          <PlanSkeleton />
          <PlanSkeleton />
          <PlanSkeleton />
        </div>
      )}

      {!loading && fetchError && (
        <p className="err-msg" style={{ marginTop: 8 }}>
          {fetchError}
        </p>
      )}

      {!loading && !fetchError && (
        <>
          {/* ── Plan cards ─────────────────────────────────────────────── */}
          <div className="grid-3">
            {groupedPlans.map((group) => {
              const isSelected = isGroupSelected(group);
              const activeVariantId = selectedVariants[group.key];
              const activeVariant =
                group.variants.find(
                  (v) => v.plan_VarriantsId === activeVariantId,
                ) || group.variants[0];

              return (
                <div
                  key={group.key}
                  className={classNames("warranty-card", {
                    selected: isSelected,
                  })}
                  onClick={() => handleGroupClick(group)}
                >
                  <div className="warranty-card__icon">
                    <img src={group.icon} alt={group.displayTitle} />
                  </div>
                  <p className="warranty-card__title">{group.displayTitle}</p>
                  <p className="warranty-card__desc">{group.cardDesc}</p>

                  {group.variants.length > 1 ? (
                    <div className="ext-chips">
                      {group.variants.map((v) => {
                        const { top, bottom } = chipLabel(v.plan_Validity);
                        return (
                          <button
                            key={v.plan_VarriantsId}
                            type="button"
                            className={classNames("ext-chip", {
                              selected:
                                isSelected &&
                                v.plan_VarriantsId === activeVariantId,
                            })}
                            onClick={(e) => handleVariantClick(e, group, v)}
                          >
                            <span>{top}</span>
                            <span>{bottom}</span>
                          </button>
                        );
                      })}
                    </div>
                  ) : (
                    <span className="warranty-card__badge">
                      {badgeLabel(group.variants[0]?.plan_Validity)}
                    </span>
                  )}

                  {activeVariant?.plan_Amount > 0 && (
                    <p
                      className="warranty-card__desc"
                      style={{
                        marginTop: 6,
                        fontWeight: 600,
                        color: "#2447AD",
                      }}
                    >
                      ₹{activeVariant.plan_Amount.toLocaleString("en-IN")}
                    </p>
                  )}
                </div>
              );
            })}
          </div>

          {/* ── Case 1: Never-Owned selected → checkbox + radio years ─── */}
          {currentlyNeverOwned && extendedGroupsForAddon.length > 0 && (
            <div className="ext-addon-box">
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: "16px",
                  flexWrap: "wrap",
                }}
              >
                <label
                  className="ext-addon-checkbox"
                  style={{
                    display: "flex",
                    alignItems: "center",
                    whiteSpace: "nowrap",
                    margin: 0,
                  }}
                >
                  <input
                    type="checkbox"
                    checked={wantExtended}
                    onChange={(e) => handleWantExtendedChange(e.target.checked)}
                  />
                  <span
                  // style={{ marginLeft: 8 }}
                  >
                    Do you want to add Extended Warranty?
                  </span>
                </label>

                {wantExtended && (
                  <div
                    className="ext-chips"
                    style={{
                      marginTop: 0,
                      display: "flex",
                      gap: "8px",
                      flexWrap: "wrap",
                    }}
                  >
                    {extendedGroupsForAddon.flatMap((eg) =>
                      eg.variants.slice(0, 2).map((v) => {
                        const isChecked =
                          selectedExtVariantId === v.plan_VarriantsId;
                        const { top, bottom } = chipLabel(v.plan_Validity);

                        return (
                          <button
                            key={v.plan_VarriantsId}
                            type="button"
                            className={classNames("ext-chip", {
                              selected: isChecked,
                            })}
                            onClick={() =>
                              handleExtRadioChange(v.plan_VarriantsId)
                            }
                          >
                            <span>{top}</span>
                            <span>{bottom}</span>
                          </button>
                        );
                      }),
                    )}
                  </div>
                )}
                {/* {wantExtended && (
                  <div
                    className="ext-chips"
                    style={{
                      marginTop: 0,
                      display: "flex",
                      gap: "8px",
                      flexWrap: "wrap",
                    }}
                  >
                    {extendedGroupsForAddon.flatMap((eg) =>
                      eg.variants.slice(0, 2).map((v) => {
                        const isChecked =
                          selectedExtVariantId === v.plan_VarriantsId;
                        const { top, bottom } = chipLabel(v.plan_Validity);

                        return (
                          <button
                            key={v.plan_VarriantsId}
                            type="button"
                            className={classNames("ext-chip", {
                              selected: isChecked,
                            })}
                            onClick={() =>
                              handleExtRadioChange(v.plan_VarriantsId)
                            }
                          >
                            <span>{top}</span>
                            <span>{bottom}</span>
                          </button>
                        );
                      }),
                    )}
                  </div>
                )} */}
              </div>
            </div>
          )}

          {/* ── Case 2: Extended Warranty card selected → two dropdowns ── */}
          {currentlyExtended &&
            currentExtGroup &&
            currentExtGroup.variants.length > 1 && (
              <div className="grid-2 mt">
                {/* Dropdown 1 — Select Extended Warranty Duration */}
                <div className="field-wrap">
                  <span className="field-label">
                    Select Extended Warranty Duration
                  </span>
                  <select
                    className="field"
                    value={selectedExtDropdownId}
                    onChange={(e) =>
                      handleExtDropdownChange(Number(e.target.value))
                    }
                  >
                    {currentExtGroup.variants.map((v) => (
                      <option
                        key={v.plan_VarriantsId}
                        value={v.plan_VarriantsId}
                      >
                        {validityText(v.plan_Validity)}
                      </option>
                    ))}
                  </select>
                </div>

                {/* Dropdown 2 — Select Existing Brand Warranty */}
                <div className="field-wrap">
                  <span className="field-label">Select Brand Warranty</span>
                  <select
                    className="field"
                    value={selectedBrandWarrantyYear}
                    onChange={(e) => {
                      const val = e.target.value;
                      setSelectedBrandWarrantyYear(val);
                      if (selectedPlan) {
                        selectPlan({ ...selectedPlan, brandWarrantyYear: val });
                      }
                    }}
                  >
                    <option value="">— Select year —</option>
                    <option value="1">1 Year</option>
                    <option value="2">2 Years</option>
                    <option value="3">3 Years</option>
                    {/* TODO: add the 4 & 5 option  */}
                    <option value="4">4 Years</option>
                    <option value="5">5 Years</option>
                    <option value="6">6 Years</option>
                    <option value="7">7 Years</option>
                    <option value="8">8 Years</option>
                    <option value="9">9 Years</option>
                    <option value="10">10 Years</option>
                  </select>
                </div>
              </div>
            )}
        </>
      )}
    </section>
  );
};

export default ProtectionPlansSection;
